import React from 'react';
import { Box, Paper, Typography, CircularProgress, Autocomplete, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, FormControl, InputLabel, Select, MenuItem } from '@mui/material';
import { ComposedChart, Bar, XAxis, YAxis, Tooltip, Legend, Line, CartesianGrid, ResponsiveContainer, LabelList, ReferenceLine } from 'recharts';
import { dashboardStyles } from '../../../styles/dashboard';
import DateRangePicker from '../common/DateRangePicker';
import SummaryCard from '../common/SummaryCard';
import { CompanyMonthlyData } from '../../../types/dashboard';
import { formatCurrency, formatNumber, getTopCompanies, filterDataByDateRange } from '../../../utils/dashboard';
import dayjs, { Dayjs } from 'dayjs';
import { useRef } from 'react';
import html2pdf from 'html2pdf.js';

interface CompanySalesTabProps {
  dateRange: {
    start: Dayjs;
    end: Dayjs;
  };
  onDateChange: (type: 'start' | 'end', value: Dayjs | null) => void;
  companyMonthlyData: CompanyMonthlyData[];
  loading: boolean;
  error: string | null;
  selectedCompany: string | null;
  onCompanyChange: (company: string | null) => void;
}

const CompanySalesTab: React.FC<CompanySalesTabProps> = ({
  dateRange,
  onDateChange,
  companyMonthlyData,
  loading,
  error,
  selectedCompany,
  onCompanyChange
}) => {
  const exportRef = useRef<HTMLDivElement>(null);

  // 기간 필터링
  const filteredCompanyData = filterDataByDateRange(
    companyMonthlyData,
    dateRange.start.format('YYYY-MM'),
    dateRange.end.format('YYYY-MM')
  ) as CompanyMonthlyData[];

  // 월별 Top10 거래처 데이터 가공
  const monthList = [...new Set(filteredCompanyData.map(row => row.month))];
  const stackedData = monthList.map(month => {
    const rows = filteredCompanyData.filter(row => row.month === month);
    const top10 = rows.sort((a, b) => b.totalBilling - a.totalBilling).slice(0, 10);
    const obj: any = { month };
    top10.forEach(row => {
      obj[row.company] = row.totalBilling;
    });
    return obj;
  });

  // topN 제외 상태
  const [excludeTopN, setExcludeTopN] = React.useState(0);

  // Top10 거래처 추출
  const top10Companies = getTopCompanies(filteredCompanyData);
  // topN 제외 적용
  const top10CompaniesExcluded = top10Companies.slice(excludeTopN);

  // 거래처 목록 추출
  const allCompanySet = new Set<string>();
  stackedData.forEach(row => {
    Object.keys(row).forEach(key => {
      if (key !== 'month') allCompanySet.add(key);
    });
  });
  const allCompanyList = Array.from(allCompanySet);

  // 선택된 거래처만 필터링
  const filteredCompanyList = selectedCompany ? [selectedCompany] : top10CompaniesExcluded;
  const filteredStackedData = stackedData.map(row => {
    const newRow: any = { month: row.month };
    filteredCompanyList.forEach(company => {
      newRow[company] = row[company] !== undefined ? row[company] : 0;
    });
    return newRow;
  });

  // 기간 문자열
  const periodStr = `${dateRange.start.format('YYYY-MM')}~${dateRange.end.format('YYYY-MM')}`;

  // 거래처 1개 선택 시: 전년 동월 대비 증감률 계산 (차트/표 공용)
  let companyMonthlyRows: CompanyMonthlyData[] = [];
  let companyMonthlyRowsWithDiff: (CompanyMonthlyData & { prevYearDiffRate: number | null })[] = [];
  let avgTotalBilling = 0;
  if (selectedCompany) {
    companyMonthlyRows = filteredCompanyData.filter(row => row.company === selectedCompany);
    companyMonthlyRowsWithDiff = companyMonthlyRows.map(row => {
      const prevYearMonth = dayjs(row.month + '-01').subtract(1, 'year').format('YYYY-MM');
      const prev = companyMonthlyRows.find(r => r.month === prevYearMonth);
      if (!prev || prev.totalBilling === 0) return { ...row, prevYearDiffRate: null };
      const diff = row.totalBilling - prev.totalBilling;
      const diffRate = (diff / prev.totalBilling) * 100;
      return { ...row, prevYearDiffRate: diffRate };
    });
    // 평균 청구운송비 계산
    avgTotalBilling =
      companyMonthlyRowsWithDiff.length > 0
        ? companyMonthlyRowsWithDiff.reduce((sum, row) => sum + row.totalBilling, 0) / companyMonthlyRowsWithDiff.length
        : 0;
  }

  // 로그: 차트 렌더링 데이터 확인
  React.useEffect(() => {
    console.log('filteredCompanyList', filteredCompanyList);
    console.log('filteredStackedData', filteredStackedData);
    console.log('monthList', monthList);
    console.log('selectedCompany', selectedCompany);
    if (filteredCompanyList.length > 0) {
      console.log('Bar company key:', filteredCompanyList[0], 'sample row:', filteredStackedData[0]);
    }
  }, [filteredCompanyList, filteredStackedData, monthList, selectedCompany]);

  // PDF 내보내기: 현재 보이는 영역을 PDF로 저장
  const handleExportPDF = () => {
    if (!exportRef.current) return;
    html2pdf().set({
      margin: 0.5,
      filename: `거래처매출대시보드_${dateRange.start.format('YYYY-MM')}_${dateRange.end.format('YYYY-MM')}.pdf`,
      html2canvas: { scale: 2 },
      jsPDF: { unit: 'cm', format: 'a4', orientation: 'landscape' },
      pagebreak: { mode: ['avoid-all'] }
    }).from(exportRef.current).save();
  };

  return (
    <>
      {/* 날짜 선택 및 합계 카드 */}
      <Paper elevation={0} sx={dashboardStyles.paper}>
        <Box sx={{ 
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DateRangePicker
            startDate={dateRange.start}
            endDate={dateRange.end}
            onStartDateChange={(date) => onDateChange('start', date)}
            onEndDateChange={(date) => onDateChange('end', date)}
          />

          {/* 거래처 다중 선택 */}
          <Autocomplete
            options={allCompanyList}
            value={selectedCompany}
            onChange={(_, v) => {
              onCompanyChange(v);
              setExcludeTopN(0); // 거래처 선택 시 제외 옵션 리셋
            }}
            size="small"
            sx={{ minWidth: 220, bgcolor: '#fff', borderRadius: 1, ml: 2 }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="거래처상호 선택"
                variant="outlined"
                InputProps={{
                  ...params.InputProps,
                  style: {
                    background: '#fff',
                    borderRadius: 8,
                    fontWeight: 700,
                    fontSize: 16,
                    color: '#222',
                  },
                }}
                InputLabelProps={{
                  ...params.InputLabelProps,
                  style: {
                    color: '#222',
                    fontWeight: 700,
                    fontSize: 16,
                    background: '#fff',
                    padding: '0 4px',
                    zIndex: 1,
                  },
                }}
              />
            )}
          />

          {/* topN 제외 드롭다운 */}
          <FormControl size="small" sx={{ minWidth: 120, ml: 1, bgcolor: '#fff', borderRadius: 1 }}>
            <InputLabel
              sx={{
                color: '#222',
                fontWeight: 700,
                fontSize: 16,
                background: '#fff',
                padding: '0 4px',
                zIndex: 1
              }}
            >제외 옵션</InputLabel>
            <Select
              value={excludeTopN}
              label="제외 옵션"
              onChange={e => setExcludeTopN(Number(e.target.value))}
              sx={{
                background: '#fff',
                borderRadius: 1,
                fontWeight: 700,
                fontSize: 16,
                color: '#222',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ffc107',
                  borderWidth: 2
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff4444',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#ff4444',
                  borderWidth: 2
                }
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: '#fff',
                    color: '#222',
                    fontWeight: 700,
                    fontSize: 16
                  }
                }
              }}
            >
              <MenuItem value={0}>제외 없음</MenuItem>
              <MenuItem value={1}>Top1 제외</MenuItem>
              <MenuItem value={2}>Top2 제외</MenuItem>
              <MenuItem value={3}>Top3 제외</MenuItem>
            </Select>
          </FormControl>

          {/* PDF 버튼 추가 */}
          <Box sx={{ display: 'flex', gap: 1, ml: 1 }}>
            <button
              style={{ background: '#1976d2', color: '#fff', border: 'none', borderRadius: 5, padding: '8px 18px', fontWeight: 600, cursor: 'pointer' }}
              onClick={handleExportPDF}
            >
              PDF
            </button>
          </Box>

          {/* 합계 정보 카드 */}
          {filteredCompanyData.length > 0 && (
            <Box sx={{ 
              display: 'flex',
              gap: 1,
              ml: 'auto'
            }}>
              <SummaryCard
                title="거래처 Top10 매출 합계"
                value={top10Companies.reduce((sum, company) => {
                  const companyData = filteredCompanyData.filter(row => row.company === company);
                  return sum + companyData.reduce((acc, row) => acc + row.totalBilling, 0);
                }, 0)}
                color="#ff4444"
              />
            </Box>
          )}
        </Box>
      </Paper>

      {/* 차트+표 전체 영역을 exportRef로 감싸기 */}
      <div ref={exportRef} style={{ display: 'flex', flexDirection: 'column', gap: 8, overflow: 'visible' }}>
        {/* 차트 영역 */}
        <Paper elevation={0} sx={dashboardStyles.paper}>
          <Typography variant="h6" sx={dashboardStyles.title}>
            월별 거래처 매출액 
          </Typography>
          {loading ? (
            <Box sx={dashboardStyles.loadingContainer}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ color: 'error.main', p: 2 }}>{error}</Box>
          ) : stackedData.length > 0 ? (
            <Box sx={{ height: 350 }}>
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={selectedCompany ? companyMonthlyRowsWithDiff : filteredStackedData} margin={{ top: 40, right: 20, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                  <XAxis dataKey="month" tick={{ fill: '#888', fontSize: 12 }} />
                  <YAxis 
                    tick={{ fill: '#888', fontSize: 12 }} 
                    tickFormatter={v => formatCurrency(v)} 
                    domain={[0, dataMax => dataMax * 1.15]}
                    yAxisId="left"
                  />
                  <Tooltip 
                    content={({ active, payload, label }) => {
                      if (!active || !payload || payload.length === 0) return null;
                      return (
                        <div style={{ background: '#2a2a2a', border: '1px solid #333', color: '#fff', padding: 10, borderRadius: 6 }}>
                          <div style={{ fontWeight: 700, marginBottom: 4 }}>{label}</div>
                          {payload.map((bar, idx) => (
                            bar.dataKey && typeof bar.value === 'number' && !(`${bar.name}`.includes('추세선')) ? (
                              <div key={idx} style={{ color: bar.color, fontWeight: 700 }}>
                                {bar.name} : {formatCurrency(bar.value)}
                              </div>
                            ) : null
                          ))}
                        </div>
                      );
                    }}
                  />
                  <Legend />
                  {selectedCompany
                    ? (
                      // 거래처 1개 선택 시: Bar + LabelList
                      <Bar
                        key={selectedCompany}
                        dataKey="totalBilling"
                        name={selectedCompany}
                        fill="#ff4444"
                        radius={[4, 4, 0, 0]}
                        yAxisId="left"
                      >
                        <LabelList
                          dataKey="totalBilling"
                          position="top"
                          formatter={(value: number) => formatCurrency(value)}
                          style={{ fontSize: 14, fill: '#ff4444', fontWeight: 700, textShadow: '0 0 2px #000' }}
                        />
                        {/* 전년 동월 대비 증감률 표시 */}
                        <LabelList
                          dataKey="prevYearDiffRate"
                          position="top"
                          content={({ x, y, value, width, index }) => {
                            if (typeof value !== 'number') return null;
                            const isUp = value > 0;
                            const color = isUp ? '#ff4444' : '#2196f3';
                            const yPos = typeof y === 'number' ? y - 36 : -36;
                            const xPos = typeof x === 'number' && typeof width === 'number' ? x + width / 2 : x;
                            return (
                              <text
                                x={xPos}
                                y={yPos}
                                fill={color}
                                fontWeight="900"
                                fontSize="16"
                                textAnchor="middle"
                              >
                                {isUp ? '▲' : '▼'}{Math.abs(value).toFixed(1)}%
                              </text>
                            );
                          }}
                        />
                      </Bar>
                    )
                    : (
                      // 여러 거래처(혹은 topN 제외) 선택 시: Bar만
                      filteredCompanyList.map((company, idx) => (
                        <Bar
                          key={company}
                          dataKey={company}
                          stackId="company"
                          name={company}
                          fill={["#ff4444","#ffc107","#4caf50","#2196f3","#9c27b0","#00bcd4","#ff5722","#3f51b5","#009688","#795548"][idx % 10]}
                          radius={[4, 4, 0, 0]}
                          yAxisId="left"
                        />
                      ))
                    )
                  }
                  {selectedCompany && (
                    <Line
                      key="totalBilling-line"
                      type="monotone"
                      dataKey="totalBilling"
                      name={selectedCompany + ' 추세선'}
                      stroke="#2196f3"
                      strokeWidth={2}
                      dot={{ fill: '#2196f3', r: 3 }}
                      connectNulls
                      yAxisId="left"
                    />
                  )}
                  {selectedCompany && (
                    <ReferenceLine
                      y={avgTotalBilling}
                      yAxisId="left"
                      stroke="#00bcd4"
                      strokeDasharray="6 3"
                      label={({ viewBox }) => {
                        const { width, y } = viewBox;
                        return (
                          <text
                            x={width - 10}
                            y={y - 8}
                            fill="#00bcd4"
                            fontWeight={700}
                            fontSize={15}
                            textAnchor="end"
                          >
                            {`평균: ${formatCurrency(avgTotalBilling)}`}
                          </text>
                        );
                      }}
                    />
                  )}
                </ComposedChart>
              </ResponsiveContainer>
            </Box>
          ) : (
            <Box sx={dashboardStyles.loadingContainer}>
              데이터가 없습니다.
            </Box>
          )}
        </Paper>

        {/* 표 영역 */}
        <TableContainer component={Paper} sx={{ mt: 1, ...dashboardStyles.table }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={dashboardStyles.tableHeaderCell}>날짜</TableCell>
                <TableCell sx={dashboardStyles.tableHeaderCell}>거래처상호</TableCell>
                <TableCell sx={dashboardStyles.tableHeaderCell}>매출액</TableCell>
                <TableCell sx={dashboardStyles.tableHeaderCell}>손익</TableCell>
                {/* 비중 컬럼: 거래처상호 선택이 없을 때 항상 */}
                {!selectedCompany && (
                  <TableCell sx={dashboardStyles.tableHeaderCell}>비중</TableCell>
                )}
                {selectedCompany && (
                  <TableCell sx={dashboardStyles.tableHeaderCell}>전년 동월 대비 증감률</TableCell>
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {selectedCompany ? (
                // 거래처 1개 선택 시: 월별 상세 데이터
                companyMonthlyRowsWithDiff.map((row, idx) => (
                  <TableRow key={idx}>
                    <TableCell sx={dashboardStyles.tableCell}>{row.month}</TableCell>
                    <TableCell sx={dashboardStyles.tableCell}>{row.company}</TableCell>
                    <TableCell sx={dashboardStyles.tableCell}>{formatNumber(row.totalBilling)}</TableCell>
                    <TableCell sx={dashboardStyles.tableCell}>{formatNumber(row.profit)}</TableCell>
                    <TableCell sx={dashboardStyles.tableCell}>
                      {row.prevYearDiffRate !== null ? (
                        <span style={{ color: row.prevYearDiffRate > 0 ? '#ff4444' : '#2196f3', fontWeight: 700 }}>
                          {row.prevYearDiffRate > 0 ? '▲' : '▼'}{Math.abs(row.prevYearDiffRate).toFixed(1)}%
                        </span>
                      ) : '-'}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                // 거래처상호 선택이 없을 때: 항상 비중 컬럼 표시
                (() => {
                  // topN 제외 옵션이 적용된 경우, 표에 나오는 거래처들의 매출액 합계로 비중을 다시 계산
                  const companiesForTable = top10CompaniesExcluded;
                  const totalSum = companiesForTable.reduce((sum, company) => {
                    const companyRows = filteredCompanyData.filter(row => row.company === company);
                    return sum + companyRows.reduce((s, row) => s + row.totalBilling, 0);
                  }, 0);
                  return companiesForTable.map(company => {
                    const companyRows = filteredCompanyData.filter(row => row.company === company);
                    const totalBilling = companyRows.reduce((sum, row) => sum + row.totalBilling, 0);
                    const totalProfit = companyRows.reduce((sum, row) => sum + row.profit, 0);
                    const percent = totalSum > 0 ? (totalBilling / totalSum) * 100 : 0;
                    return (
                      <TableRow key={company}>
                        <TableCell sx={dashboardStyles.tableCell}>{periodStr}</TableCell>
                        <TableCell sx={dashboardStyles.tableCell}>{company}</TableCell>
                        <TableCell sx={dashboardStyles.tableCell}>{formatNumber(totalBilling)}</TableCell>
                        <TableCell sx={dashboardStyles.tableCell}>{formatNumber(totalProfit)}</TableCell>
                        <TableCell sx={dashboardStyles.tableCell}>{percent.toFixed(1)}%</TableCell>
                      </TableRow>
                    );
                  });
                })()
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </div>
    </>
  );
};

export default CompanySalesTab; 